import { useCallback, useEffect, useRef, useState } from "react";

import { isFeatureEnabled } from "@editor/infra/featureFlags";

import shouldShowV2Debuggers from "@/features/react-editor/utils/shouldShowV2Debuggers";

import { useDaytona } from "./daytona/useDaytona";
import { getRouteFromFilePath, normalizeRoute } from "./utils";
import {
  MOUNTING_ADDITONAL_FILES_MESSAGE,
  useWebContainerHook,
} from "./web-container/useWebContainerHook";

// biome-ignore lint/style/noEnum: I'm using the enum so that I can use greater than comparisons
export enum NextDevServerStatus {
  Error = -1,
  Booting = 1,
  WaitingForFiles = 2,
  Mounting = 3,
  Installing = 4,
  Starting = 5,
  Downloading = 6,
  Ready = 7,
  Compiling = 8,
  Serving = 9,
}

export type LogMessage = {
  date: Date;
  message: string;
};
function stripAnsiCodes(message: string): string {
  return (
    message
      // biome-ignore lint/suspicious/noControlCharactersInRegex: we are specifically looking for control characters
      .replace(/\x1b\[[\d;]*m/g, "") // Remove color codes
      .trim()
  );
}

type PageStatusRecord = {
  timestamp: number;
  status: number;
  hasImportError?: boolean;
};
type PageStatusMap = Record<string, PageStatusRecord>;

type UseNextDevServerReturn = {
  logs: LogMessage[];
  url: string | undefined;
  /**
   * A map of page routes to their latest status resulting from a GET request.
   * This is useful for seeing what pages have been rendered and what their
   * status was when they were rendered. Statuses are 200, 404, 500, etc. it
   * also includes a boolean flag indicating whether the page had an import
   * error - if the page had an import error then it should not be rendered.
   * Doing so will break the dev server and render all pages broken.
   */
  pageStatusMap: PageStatusMap;
  /**
   * A key that is incremented when fast refresh performs a full reload. This
   * can be used to force a reload of the iframe.
   */
  refreshEpoch: number;
  status: NextDevServerStatus;

  invalidatePageImportErrors: (pageRoutes: string[]) => void;
  error: unknown | null;

  /**
   * Status of mounting additional files (which is used for incremental updates, e.g.
   * modifier updates, etc)
   */
  mountAdditionalFilesStatus: "notStarted" | "mounting" | "compiled";

  // NOTE (Gabe 2025-09-08): These are only really relevant for WebContainers,
  // so shouldn't be here, but to avoid even more refactoring I'm leaking this
  // abstraction for now. It's possible that we'll want to use exportFiles or
  // something across providers at some point in which case we should probably
  // use some non-WebContainer data structure.
  mountAdditionalFiles?: (files: any) => Promise<void>;
  exportFileTree?: (exportDir: "full" | "workdir") => Promise<any>;
};

type UseNextDevServerParams = {
  projectId: string | undefined;
  hostingType?: "replo" | "shopify";
};
export function useNextDevServerHook({
  projectId,
  hostingType,
}: UseNextDevServerParams): UseNextDevServerReturn {
  const [status, setStatus] = useState<NextDevServerStatus>(
    NextDevServerStatus.Booting,
  );

  const isNextServerReady = status >= NextDevServerStatus.Ready;

  const [pageStatusMap, setPageStatusMap] = useState<PageStatusMap>({});
  const invalidatePageImportErrors = useCallback((pageRoutes: string[]) => {
    setPageStatusMap((prev) => {
      const updated = { ...prev };
      for (const route of pageRoutes) {
        const normalizedRoute = normalizeRoute(route);
        if (updated[normalizedRoute]) {
          updated[normalizedRoute] = {
            ...updated[normalizedRoute],
            hasImportError: undefined,
          };
        }
      }
      return updated;
    });
  }, []);

  const [mountAdditionalFilesStatus, setMountAdditionalFilesStatus] = useState<
    "notStarted" | "mounting" | "compiled"
  >("notStarted");

  const [logs, setLogs] = useState<LogMessage[]>([]);

  // NOTE (Gabe 2025-08-12): Sometimes fast refresh has to perform a full
  // reload. For some reason, when this happens iframes displaying the content
  // don't reload. We can force them to reload by incrementing this key.
  const [refreshEpoch, setRefreshEpoch] = useState(0);

  // no state needed to correlate lines; Next's error line contains both context and message

  // NOTE (Gabe 2025-08-13): This is a ref because processLogMessage needs to
  // use it, and it not being a ref would introduce a cyclical dependency.
  const restartDevServerRef = useRef<(() => Promise<void>) | null>(null);

  const processLogMessage = useCallback((message: string) => {
    const cleanedMessage = stripAnsiCodes(message);

    if (!cleanedMessage) {
      return;
    }

    if (cleanedMessage.includes("Starting development server")) {
      setStatus(NextDevServerStatus.Installing);
    }

    if (cleanedMessage.includes("✓ Ready in")) {
      setStatus(NextDevServerStatus.Ready);
    }

    if (cleanedMessage.includes("Compiling")) {
      setStatus(NextDevServerStatus.Compiling);
    }

    if (cleanedMessage.includes("Downloading swc package")) {
      setStatus(NextDevServerStatus.Downloading);
    }

    if (cleanedMessage.includes("✓ Compiled in ")) {
      setMountAdditionalFilesStatus("compiled");
    }

    // COMMON
    if (cleanedMessage.startsWith("GET")) {
      setStatus(NextDevServerStatus.Serving);
      const [, pageRoute, status] = cleanedMessage.split(" ");
      const statusNumber = Number(status);
      if (!pageRoute || isNaN(statusNumber)) {
        return;
      }
      const normalizedRoute = normalizeRoute(pageRoute);
      setMountAdditionalFilesStatus("compiled");
      setPageStatusMap((prev) => {
        const existing = prev[normalizedRoute];
        // Import errors have already been handled by the previous "Module not
        // found" message that came through.
        if (existing?.hasImportError) {
          return prev;
        }
        return {
          ...prev,
          [normalizedRoute]: {
            timestamp: Date.now(),
            status: statusNumber,
            hasImportError: false,
          },
        };
      });
    }

    if (cleanedMessage.startsWith(MOUNTING_ADDITONAL_FILES_MESSAGE)) {
      setMountAdditionalFilesStatus("mounting");
    }

    // NOTE (Gabe 2025-09-08): These module errors should not be encountered by
    // newly generated code ever since we enabled the noUnresolvedImports biome
    // rule.
    //
    // When a module-not-found error occurs, mark the route as 500 +
    // importError
    if (cleanedMessage.includes("Module not found: Can't resolve")) {
      // Parse file path from the same log message, e.g.:
      // "⨯ ./app/product-page-4/PageClientComponent.tsx:4:9\nModule not found: Can't resolve '...'")
      const match = cleanedMessage.match(/\.\/app\/([^\s:]+):\d+:\d+/);
      const filePath = match?.[1] ?? null;

      if (filePath) {
        const routePath = getRouteFromFilePath(filePath);
        const normalized = normalizeRoute(routePath);
        setPageStatusMap((prev) => ({
          ...prev,
          [normalized]: {
            timestamp: Date.now(),
            status: 500,
            hasImportError: true,
          },
        }));
        processLogMessage(
          `Module resolution error detected for route ${normalized} - restarting dev server...`,
        );
        void restartDevServerRef.current?.();
      }
    }

    // WebContainer
    // #region Error cases
    if (
      cleanedMessage.startsWith("⚠ Fast Refresh had to perform a full reload")
    ) {
      setRefreshEpoch((k) => k + 1);
    }

    if (
      cleanedMessage.includes(
        "Detected change, manual restart required due to '__NEXT_DISABLE_MEMORY_WATCHER' usage",
      )
    ) {
      void restartDevServerRef.current?.();
    }

    // COMMON

    // #endregion

    setLogs((prev) => [...prev, { date: new Date(), message: cleanedMessage }]);
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "NEXTJS_DEV_INDICATOR_OVERRIDE_REQUEST") {
        event.source?.postMessage(
          {
            type: "NEXTJS_DEV_INDICATOR_OVERRIDE_RESPONSE",
            value: shouldShowV2Debuggers(),
          },
          { targetOrigin: event.origin || "*" },
        );
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const isServerSandboxEnabled = isFeatureEnabled("use-server-sandbox");

  const incrementRefreshEpoch = useCallback(() => {
    setRefreshEpoch((k) => k + 1);
  }, []);

  const {
    url: serverSandboxUrl,
    restartDevServer: restartDevServerSandbox,
    error: serverSandboxError,
  } = useDaytona({
    // Note (Noah, 2025-09-12): Skip fetching daytona if (1) we don't have the flag enabled or (2) we're a v1 project
    forceSkip: !isServerSandboxEnabled || hostingType !== "replo",
    projectId: isServerSandboxEnabled ? projectId : undefined,
    processLogMessage,
    incrementRefreshEpoch,
  });
  const {
    url: webContainerUrl,
    mountAdditionalFiles,
    exportFileTree,
    restartDevServer: restartDevServerWebContainer,
    error: webContainerError,
  } = useWebContainerHook({
    projectId: isServerSandboxEnabled ? undefined : projectId,
    // Note (Noah, 2025-09-12): Skip fetching webcontainer content if we're not a v2 project
    // or if we're not using webcontainers
    forceSkip: isServerSandboxEnabled || hostingType !== "replo",
    processLogMessage,
  });

  const url = isServerSandboxEnabled ? serverSandboxUrl : webContainerUrl;

  useEffect(() => {
    restartDevServerRef.current = isServerSandboxEnabled
      ? restartDevServerSandbox
      : restartDevServerWebContainer;
  }, [
    isServerSandboxEnabled,
    restartDevServerSandbox,
    restartDevServerWebContainer,
  ]);

  return {
    logs,
    status,
    refreshEpoch,
    url: isNextServerReady ? url : undefined,
    pageStatusMap,
    invalidatePageImportErrors,
    error: isServerSandboxEnabled ? serverSandboxError : webContainerError,
    exportFileTree: isServerSandboxEnabled ? undefined : exportFileTree,
    mountAdditionalFiles: isServerSandboxEnabled
      ? // Note (Noah, 2025-09-28): Even if server sandbox is enabled, we still log this message
        // since we currently rely on it to know when to show the modifiers loading state.
        async () => {
          processLogMessage(MOUNTING_ADDITONAL_FILES_MESSAGE);
        }
      : mountAdditionalFiles,
    mountAdditionalFilesStatus,
  };
}
